// frontend/src/hooks/useCollapsiblePanel.ts
import { useCallback, useState } from 'react';

/**
 * Collapse state for an editor panel, remembered across reloads.
 *
 * Lifted verbatim out of FieldListSidebar, which has carried this since the
 * field list replaced the old bottom "Form Fields" grid. The properties
 * inspector now needs the same behaviour, and the observable behaviour has to
 * be IDENTICAL - collapse to a vertical-text rail, remember the choice - because
 * an author who learns it on one panel should not have to learn it again on the
 * other. Copying it would have been two implementations free to drift; this is
 * one.
 *
 * Persistence is also an accessibility point, not just a convenience: below the
 * reflow breakpoint the panels are in the flow and stacked, and a user who
 * collapses one to get the page back should not have to do it again on every
 * reload (WCAG 1.4.10).
 *
 * @param storageKey localStorage key. Give each panel INSTANCE its own - the
 *   editor and the live preview each render a field list, and one shared key
 *   would collapse both.
 * @returns `[collapsed, toggleCollapsed, dismissTransiently]` — the third collapses
 *   without persisting; see its comment.
 */
export function useCollapsiblePanel(storageKey: string): [boolean, () => void, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch {
      // Storage disabled or blocked (private mode, hardened browser). The panel
      // still works; it just forgets the choice.
      return false;
    }
  });

  // Stable identity (functional setState needs nothing from the closure but
  // the key). PDFViewer's window Esc handler now lists this in its effect
  // dependencies, and a new identity every render would churn that listener
  // for no reason — the same argument applyFullscreen already records there.
  const toggleCollapsed = useCallback(() => {
    setCollapsed(prev => {
      try {
        localStorage.setItem(storageKey, String(!prev));
      } catch {
        // non-fatal — see above
      }
      return !prev;
    });
  }, [storageKey]);

  /**
   * Collapse WITHOUT remembering it — the dismissal half of the owner's 6 Oct
   * ruling: "The ESC button should be a dismissal and cancel versus a save."
   * The retract BUTTON states a preference and persists (toggleCollapsed
   * above); Esc dismisses a surface that was in the way and must leave the
   * stored preference alone, so the next session opens the way the author
   * actually configured it. State and storage diverging here is the point,
   * not a bug: the divergence lasts exactly one session, which is what
   * "transient" means.
   */
  const dismissTransiently = useCallback(() => {
    setCollapsed(true);
  }, []);

  return [collapsed, toggleCollapsed, dismissTransiently];
}

export default useCollapsiblePanel;
